import React from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * ModernCard - Card moderno com gradiente e efeitos visuais
 * Usado para criar consistência visual em todo o sistema
 */
export const ModernCard = ({ 
  children, 
  className = '', 
  gradient = 'from-slate-700 to-slate-800',
  onClick,
  hover = true,
  padding = 'p-5'
}) => {
  return (
    <div 
      className={`
        relative overflow-hidden rounded-2xl bg-gradient-to-br ${gradient}
        ${hover ? 'transition-all duration-300 hover:scale-[1.01] hover:shadow-xl' : ''}
        ${onClick ? 'cursor-pointer' : ''}
        ${padding}
        ${className}
      `}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

/**
 * ModernStatCard - Card para exibir estatísticas
 */
export const ModernStatCard = ({
  icon: Icon,
  value,
  label,
  gradient = 'from-blue-600 to-blue-800',
  onClick,
  trend,
  suffix = ''
}) => {
  return (
    <div 
      className={`
        group relative cursor-pointer overflow-hidden rounded-2xl h-32
        transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl
        ${onClick ? 'cursor-pointer' : ''}
      `}
      onClick={onClick}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
      
      {/* Ícone decorativo grande no fundo */}
      {Icon && (
        <div className="absolute -right-4 -bottom-4 opacity-20 group-hover:opacity-30 transition-opacity">
          <Icon className="h-28 w-28 text-white" />
        </div>
      )}
      
      <div className="absolute inset-0 p-4 flex flex-col justify-between">
        <div className="flex items-center justify-between">
          {Icon && (
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl group-hover:scale-110 transition-transform">
              <Icon className="h-5 w-5 text-white" />
            </div>
          )}
          {onClick && (
            <ChevronRight className="h-5 w-5 text-white/60 group-hover:translate-x-1 group-hover:text-white transition-all" />
          )}
        </div>
        <div>
          <p className="text-2xl lg:text-3xl font-bold text-white">
            {value}{suffix}
          </p>
          <p className="text-sm text-white/80 font-medium">{label}</p>
          {trend && (
            <p className={`text-xs mt-1 ${trend > 0 ? 'text-green-300' : 'text-red-300'}`}>
              {trend > 0 ? '+' : ''}{trend}% vs mês anterior
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * ModernListCard - Card para listas de itens
 */
export const ModernListCard = ({
  children,
  title,
  subtitle,
  icon: Icon,
  status,
  statusColor = 'bg-gray-500',
  actions,
  onClick,
  className = '',
  gradient = 'from-slate-700/50 to-slate-800/50'
}) => {
  const statusColors = {
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
    danger: 'bg-red-500',
    info: 'bg-blue-500',
    default: 'bg-gray-500'
  };

  return (
    <div 
      className={`
        group relative overflow-hidden rounded-xl bg-gradient-to-br ${gradient}
        border border-white/5 backdrop-blur-sm
        transition-all duration-300 hover:border-white/10 hover:shadow-lg
        ${onClick ? 'cursor-pointer' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Ícone ou Imagem */}
          {Icon && (
            <div className="p-3 bg-white/10 rounded-xl shrink-0">
              <Icon className="h-6 w-6 text-white" />
            </div>
          )}
          
          {/* Conteúdo */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                {title && (
                  <h3 className="font-semibold text-white truncate">{title}</h3>
                )}
                {subtitle && (
                  <p className="text-sm text-gray-400 truncate">{subtitle}</p>
                )}
              </div>
              
              {status && (
                <span className={`
                  px-3 py-1 rounded-full text-xs font-medium text-white shrink-0
                  ${statusColors[statusColor] || statusColors.default}
                `}>
                  {status}
                </span>
              )}
            </div>
            
            {/* Conteúdo adicional */}
            {children && (
              <div className="mt-3 text-sm text-gray-300">
                {children}
              </div>
            )}
          </div>
        </div>
        
        {/* Ações */}
        {actions && (
          <div className="mt-4 pt-3 border-t border-white/5 flex flex-wrap gap-2">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ModernButton - Botão com estilo moderno
 */
export const ModernButton = ({
  children,
  variant = 'primary',
  size = 'md',
  icon: Icon,
  onClick,
  disabled = false,
  className = '',
  ...props
}) => {
  const variants = {
    primary: 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white',
    secondary: 'bg-white/10 hover:bg-white/20 text-white border border-white/20',
    danger: 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white',
    warning: 'bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 text-white',
    ghost: 'bg-transparent hover:bg-white/10 text-gray-300 hover:text-white',
    success: 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white',
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  return (
    <button
      className={`
        inline-flex items-center justify-center gap-2 rounded-xl font-medium
        transition-all duration-200 
        ${variants[variant]}
        ${sizes[size]}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {Icon && <Icon className="h-4 w-4" />}
      {children}
    </button>
  );
};

/**
 * ModernBadge - Badge/Tag com estilo moderno
 */
export const ModernBadge = ({
  children,
  variant = 'default',
  className = ''
}) => {
  const variants = {
    default: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
    success: 'bg-green-500/20 text-green-400 border-green-500/30',
    warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    danger: 'bg-red-500/20 text-red-400 border-red-500/30',
    info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  };

  return (
    <span className={`
      inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium
      border ${variants[variant]} ${className}
    `}>
      {children}
    </span>
  );
};

/**
 * ModernPageHeader - Cabeçalho de página moderno
 */
export const ModernPageHeader = ({
  title,
  subtitle,
  icon: Icon,
  actions,
  gradient = 'from-slate-800 to-slate-900'
}) => {
  return (
    <div className={`
      relative overflow-hidden rounded-2xl bg-gradient-to-br ${gradient}
      p-6 mb-6 border border-white/5
    `}>
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32" />
      
      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {Icon && (
            <div className="p-3 bg-white/10 rounded-xl">
              <Icon className="h-6 w-6 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-white">{title}</h1>
            {subtitle && (
              <p className="text-gray-400 text-sm mt-1">{subtitle}</p>
            )}
          </div>
        </div>
        
        {actions && (
          <div className="flex items-center gap-3">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default {
  ModernCard,
  ModernStatCard,
  ModernListCard,
  ModernButton,
  ModernBadge,
  ModernPageHeader
};
